import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './OverloadSection.css';

function usePrefersReducedMotionOrMobile() {
  const prefersReducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const listener = () => setIsMobile(mq.matches);
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, []);
  return Boolean(prefersReducedMotion) || isMobile;
}

const floatingPhrases = [
  { id: 1, text: 'Too much to hold at once', x: '15%', y: '20%', delay: 0 },
  { id: 2, text: 'Ideas passing too quickly', x: '25%', y: '15%', delay: 0.3 },
  { id: 3, text: 'Somewhere in my head…', x: '35%', y: '25%', delay: 0.6 },
  { id: 4, text: 'I’ll remember later', x: '70%', y: '30%', delay: 0.9 },
  { id: 5, text: 'So many directions to think about', x: '60%', y: '20%', delay: 1.2 },
  { id: 6, text: 'I had a thought just now', x: '80%', y: '25%', delay: 1.5 },
];

function OverloadSection() {
  const reduceMotion = usePrefersReducedMotionOrMobile();

  const floatVariants = {
    float: {
      y: [0, -15, 0],
      rotate: [0, 5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-100px' },
    transition: { duration: 0.8, ease: 'easeOut' },
  };

  const phraseTransition = reduceMotion
    ? { duration: 0.3, ease: 'easeOut' }
    : { duration: 0.5, type: 'spring', stiffness: 200 };

  return (
    <section className="overload-section">
      <div className="overload-container">
        <motion.div
          className="overload-intro"
          {...fadeInUp}
        >
          <p className="overload-intro-text">
            Your head is full of ideas, tasks, and reminders.
          </p>
          <p className="overload-intro-text">
            Nothing is missing.
          </p>
        </motion.div>

        <div className="overload-main">
          <motion.h2
            className="overload-headline"
            {...fadeInUp}
            transition={{ delay: 0.2 }}
          >
            It just needs a place to{' '}
            <span className="overload-headline-word">organize</span>
          </motion.h2>

          <motion.div
            className="overload-emoji-container"
            {...fadeInUp}
            transition={{ delay: 0.4 }}
            variants={floatVariants}
            animate={reduceMotion ? false : 'float'}
          >
            <div className="overload-emoji-3d">
              <div className="emoji-character">🫣</div>
            </div>
          </motion.div>

          {floatingPhrases.map((phrase, index) => (
            <motion.div
              key={phrase.id}
              className="overload-phrase-bubble"
              style={{
                left: phrase.x,
                top: phrase.y,
              }}
              initial={{ opacity: 0, scale: reduceMotion ? 1 : 0, rotate: reduceMotion ? 0 : -10 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ ...phraseTransition, delay: phrase.delay + (reduceMotion ? 0 : 0.6) }}
              variants={floatVariants}
              animate={reduceMotion ? false : 'float'}
            >
              {phrase.text}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="overload-3d-shapes"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: reduceMotion ? 0 : 1, duration: reduceMotion ? 0.2 : 1 }}
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`overload-shape overload-shape--${i % 3 === 0 ? 'cube' : i % 3 === 1 ? 'sphere' : 'pyramid'}`}
              style={{
                left: `${20 + i * 12}%`,
                top: `${10 + (i % 2) * 15}%`,
              }}
              variants={floatVariants}
              animate={reduceMotion ? false : 'float'}
              transition={{
                delay: i * 0.2,
                duration: 3 + i * 0.5,
              }}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default OverloadSection;
